import { useCallback, useEffect, useRef, useState, MutableRefObject } from 'react'
import { File as FileType, Maybe } from 'types'
import { GENERIC_ERROR, LIMIT_ERROR, LIMIT_TEXT, MAX_FILE_SIZE } from './Constants'
import { getParamsForFileUpload } from 'utils'
import { convertToMB, getFileNameProperties } from './Utils'
import { ReactComponent as FileIcon } from 'assets/icons/attachment.svg'
import Styles from './File.module.scss'
import cx from 'classnames'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

export const FileInput = ({
  updateAttrs,
  attrs,
  fileCardRef,
}: {
  attrs: { [key: string]: any }
  updateAttrs: (properties: { [key: string]: any }) => void
  fileCardRef: MutableRefObject<Maybe<HTMLDivElement>>
}) => {
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<FileType>()
  const [shouldUpload, setShouldUpload] = useState(false)
  const inputRef = useRef<Maybe<HTMLInputElement>>(null)

  const openFileChooser = () => {
    if (uploading) return
    inputRef.current?.click()
  }

  const handleFileSelection = () => {
    if (!inputRef.current?.files) return

    const selectedFile = inputRef.current?.files[0]
    setSelectedFile(selectedFile)
  }

  useEffect(() => {
    if (!fileCardRef.current) return
    const card = fileCardRef.current
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      fileCardRef.current?.classList.remove(Styles.selectedInput)
      const dt = e.dataTransfer
      const file = dt?.files[0]

      setSelectedFile(file)
    }

    const handleDragEnter = () => {
      fileCardRef.current?.classList.add(Styles.selectedInput)
    }

    const handleDragLeave = () => {
      fileCardRef.current?.classList.remove(Styles.selectedInput)
    }

    card.addEventListener('dragenter', handleDragEnter, false)
    card.addEventListener('dragleave', handleDragLeave, false)
    card.addEventListener('drop', handleDrop, false)

    return () => {
      card.removeEventListener('drop', handleDrop, false)
      card.removeEventListener('dragenter', handleDragEnter, false)
      card.removeEventListener('dragleave', handleDragLeave, false)
    }
  }, [fileCardRef])

  const handleError = (errorMessage: string) => {
    showErrorMessageToast(errorMessage)
    setShouldUpload(false)
    setSelectedFile(undefined)
    setUploading(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleUpload = useCallback(async () => {
    setUploading(true)
    const res = await getParamsForFileUpload(selectedFile)

    if (!res || !selectedFile) {
      return
    }
    const { url, method, body, error } = res

    if (error || !url) {
      handleError(LIMIT_ERROR)
      return
    }

    const fileNameProperties = getFileNameProperties(selectedFile.name)

    if (!fileNameProperties) return

    const { extension } = fileNameProperties
    const { name } = selectedFile

    try {
      const res = await fetch(url, {
        method,
        body,
        headers: { 'Content-Disposition': `attachment` },
      })
      if (res.ok) {
        updateAttrs({
          ...attrs,
          url: res.url.split('?')[0],
          title: name,
          format: extension,
          description: name,
          meta: JSON.stringify({
            name,
            extension,
            size: selectedFile.size,
          }),
        })
        setUploading(false)
        setSelectedFile(undefined)
      } else {
        handleError(GENERIC_ERROR)
      }
    } catch {
      handleError(GENERIC_ERROR)
    }
  }, [attrs, selectedFile, updateAttrs])

  useEffect(() => {
    if (!shouldUpload) return

    handleUpload()
  }, [shouldUpload, handleUpload])

  useEffect(() => {
    if (!selectedFile) return
    const { size } = selectedFile

    if (convertToMB(size) <= MAX_FILE_SIZE) {
      setShouldUpload(true)
    } else {
      handleError(LIMIT_ERROR)
    }
  }, [selectedFile])

  return (
    <div onClick={openFileChooser} ref={fileCardRef} className={cx(Styles.fileCard, Styles.emptyFileInput)}>
      <div className={Styles.left}>
        {!uploading && (
          <div>
            <FileIcon className={Styles.fileIcon} />
          </div>
        )}
        <div>
          <input onChange={handleFileSelection} type="file" style={{ display: 'none' }} ref={inputRef} />
          <span className={Styles.inputTextPrompt} aria-label="Drag file here or choose a file to upload" role="form">
            {uploading && (
              <div className={Styles.uploadingIndicator}>
                <p className={Styles.uploadingIndicatorText} aria-label="File uploading">
                  Uploading...
                </p>
              </div>
            )}
            {!selectedFile && (
              <>
                Drag file here or <span className={Styles.fileCTA}>choose a file</span>
              </>
            )}
          </span>
        </div>
      </div>
      <div className={Styles.right}>
        {!uploading && (
          <span className={Styles.fileSizeLimit} aria-label={`File upload size limit is ${LIMIT_TEXT}`}>
            {LIMIT_TEXT} Limit
          </span>
        )}
      </div>
    </div>
  )
}
